import * as React from "react";
import { useMutation } from "react-query";
import { ClientsService, CustomFieldDefinition, CustomFieldDefinitionCategory } from "gen/clients";
import * as Yup from "yup";
import { Formik } from "formik";
import Drawer from "@mui/material/Drawer";
import { DrawerHeader } from "../../../../../components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "../../../../../components/DrawerBody/DrawerBody";
import { CustomFieldForm, FieldName, FormValues } from "./components/CustomFieldForm/CustomFieldForm";
import { SelectOption, SelectOptionGroup } from "@mui/base";
import { SnackbarApiError } from "../../../../../components/SnackbarApiError/SnackbarApiError";
import { CustomFieldUtils } from "./CustomFieldUtils";

interface Props {
  clientId: string;
  customFieldDefinitions: CustomFieldDefinition[];
  editedFieldIndex: number;
  open: boolean;
  mappingOptions: SelectOptionGroup<string>[];
  otherFieldsForVisibility?: {
    fieldName: string;
    possibleValues?: SelectOption<string>[];
  }[];
  onClose: () => void;
  onSuccess: () => void;
}

const EditCustomFieldPanel: React.FC<Props> = ({
  clientId,
  customFieldDefinitions,
  editedFieldIndex,
  open,
  mappingOptions,
  otherFieldsForVisibility,
  onSuccess,
  onClose
}) => {
  const editedField: CustomFieldDefinition | undefined = customFieldDefinitions[editedFieldIndex];
  if (!editedField) {
    throw new Error(`Invalid field index: ${editedFieldIndex}`);
  }

  const { mutate, error } = useMutation("updateCustomFieldDefinitions", {
    onSuccess,
    mutationFn: ClientsService.updateCustomFieldDefinitions
  });
  const [options, setOptions] = React.useState<SelectOption<string>[]>(editedField.options || []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Required"),
    mapping: Yup.mixed().test("is-not-empty", "Required", value => (Array.isArray(value) ? value.length > 0 : !!value))
  });

  const validate = React.useCallback(
    (formValues: FormValues) => {
      const errors: Record<string, string> = {};
      if (
        formValues.name !== editedField.name &&
        customFieldDefinitions.find(cfd => cfd.name === formValues.name.trim())
      ) {
        errors.name =
          "A field with this name already exists. Name must be unique across all Project and User custom fields.";
      }
      if (CustomFieldUtils.requiresOptions(formValues.type) && options.length === 0) {
        errors.options = "At least one option is required for this field type.";
      }
      return errors;
    },
    [customFieldDefinitions, editedField.name, options]
  );

  const onSubmit = React.useCallback(
    (formValues: FormValues) => {
      const projectCustomFieldDefinitions: CustomFieldDefinition[] = customFieldDefinitions || [];
      const fieldDefinition = projectCustomFieldDefinitions[editedFieldIndex];
      if (!fieldDefinition) {
        throw new Error(`Invalid field index: ${editedFieldIndex}`);
      }
      fieldDefinition.name = formValues.name.trim();
      fieldDefinition.type = formValues.type;
      fieldDefinition.required = formValues.required;
      fieldDefinition.options =
        options.length > 0
          ? options.map(o => ({
              label: o.label as string,
              value: o.value
            }))
          : undefined;
      fieldDefinition.defaultValue = formValues.defaultValue.trim() || undefined;
      fieldDefinition.mapping = formValues.mapping || undefined;
      fieldDefinition.visible = formValues.visible === "VISIBLE";
      fieldDefinition.visibleIf =
        formValues[FieldName.VISIBLE] === "CONDITIONAL"
          ? [
              {
                fieldName: formValues[FieldName.VISIBLE_IF_FIELD_NAME],
                value: formValues[FieldName.VISIBLE_IF_FIELD_VALUE]
              }
            ]
          : [];
      fieldDefinition.validationRegex = formValues.validationRegex.trim() || undefined;
      fieldDefinition.validationMessage = formValues.validationMessage.trim() || undefined;
      fieldDefinition.description = formValues.description.trim() || undefined;

      mutate({
        clientId,
        requestBody: projectCustomFieldDefinitions
      });
    },
    [options, editedFieldIndex, clientId, mutate, customFieldDefinitions]
  );

  return (
    <Drawer open={open} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 800 } }}>
      <DrawerHeader title="Edit Custom Field" onClose={onClose} />
      <DrawerBody>
        {!!error && <SnackbarApiError error={error} />}
        <Formik
          initialValues={{
            [FieldName.NAME]: editedField.name,
            [FieldName.TYPE]: editedField.type,
            [FieldName.REQUIRED]: editedField.required || false,
            [FieldName.DEFAULT_VALUE]: editedField.defaultValue || "",
            [FieldName.MAPPING]:
              typeof editedField.mapping === "string" ? [editedField.mapping] : editedField.mapping || [],
            [FieldName.VISIBLE]: editedField.visible
              ? "VISIBLE"
              : editedField.visibleIf?.length
                ? "CONDITIONAL"
                : "HIDDEN",
            [FieldName.VISIBLE_IF_FIELD_NAME]: editedField.visibleIf?.[0]?.fieldName || "",
            [FieldName.VISIBLE_IF_FIELD_VALUE]: editedField.visibleIf?.[0]?.value || "",
            [FieldName.VALIDATION_REGEX]: editedField.validationRegex || "",
            [FieldName.VALIDATION_MESSAGE]: editedField.validationMessage || "",
            [FieldName.DESCRIPTION]: editedField.description || ""
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          validate={validate}
        >
          <CustomFieldForm
            options={options}
            setOptions={setOptions}
            mappingOptions={mappingOptions}
            onCancel={onClose}
            allowTextArea={editedField.category === CustomFieldDefinitionCategory.PROJECT}
            otherFieldsForVisibility={otherFieldsForVisibility}
          />
        </Formik>
      </DrawerBody>
    </Drawer>
  );
};

export { EditCustomFieldPanel };
